import { useState, useRef, useCallback } from "react";
import { publicApi } from "../api/publicClient";

const VALID_RE = /^[A-Za-z\s.'-]+$/;

const colors = {
  blue900: "#0f2d63", blue700: "#1d4ed8", blue600: "#2563eb",
  blue100: "#e6edfb", blue50: "#f3f6fd",
  ink900: "#10151f", ink400: "#8892a6",
  line: "#e6e9f0", white: "#ffffff", grey100: "#f1f3f8",
};

const styles = {
  body: {
    margin: 0,
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    background: colors.grey100,
    color: colors.ink900,
    minHeight: "100vh",
  },
  topbar: {
    background: colors.blue900,
    color: colors.white,
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 700,
  },
  topbarImg: { width: 24, height: 24, objectFit: "contain", borderRadius: 6 },
  layoutNarrow: { maxWidth: 480, margin: "0 auto", padding: "40px 20px 60px" },
  panel: {
    background: colors.white,
    border: `1px solid ${colors.line}`,
    borderRadius: 18,
    padding: 26,
    boxShadow: "0 1px 2px rgba(16,21,31,0.04), 0 8px 24px rgba(16,21,31,0.05)",
  },
  heading: { fontSize: 20, fontWeight: 800, marginBottom: 6 },
  sub: { color: colors.ink400, fontSize: 13.5, marginBottom: 20 },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: colors.white,
    border: `1.5px solid ${colors.line}`,
    borderRadius: 12,
    padding: "12px 14px",
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: 15,
    width: "100%",
    fontFamily: "inherit",
    background: "transparent",
  },
  hint: { fontSize: 12, color: colors.ink400, marginTop: 8 },
  results: { marginTop: 16 },
  emptyMsg: { textAlign: "center", color: colors.ink400, fontSize: 13, padding: "16px 0" },
  resultRow: {
    display: "block",
    textDecoration: "none",
    color: "inherit",
    background: colors.blue50,
    border: `1px solid ${colors.blue100}`,
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 8,
  },
  resultName: { fontWeight: 700, fontSize: 14.5 },
  resultPhone: { color: colors.ink400, fontSize: 12.5, marginTop: 2 },
};

export default function FindAccount() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [error, setError] = useState(false);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    try {
      const data = await publicApi.findAccount(q);
      setResults(data.results || []);
      setError(false);
    } catch {
      setResults(null);
      setError(true);
    }
  }, []);

  function handleChange(e) {
    const cleaned = e.target.value.replace(/[^A-Za-z\s.'-]/g, "");
    setQuery(cleaned);
    clearTimeout(debounceRef.current);

    const q = cleaned.trim();
    if (q.length < 3) {
      setResults(null);
      setError(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(q), 300);
  }

  const showHint = query.trim().length < 3;

  return (
    <div style={styles.body}>
      <div style={styles.topbar}>
        <img src="/otic-logo.png" alt="Otic" style={styles.topbarImg} />
        Payment Plans
      </div>

      <div style={styles.layoutNarrow}>
        <div style={styles.panel}>
          <div style={styles.heading}>Find your payment link</div>
          <div style={styles.sub}>Type your name below to find your account and continue to your payment page.</div>

          <div style={styles.searchBox}>
            <input
              type="text"
              placeholder="Type your full name..."
              value={query}
              onChange={handleChange}
              autoFocus
              style={styles.searchInput}
            />
          </div>
          {showHint && <div style={styles.hint}>Type at least 3 letters of your name.</div>}

          <div style={styles.results}>
            {error && <div style={styles.emptyMsg}>Something went wrong. Please try again.</div>}
            {!error && results && results.length === 0 && (
              <div style={styles.emptyMsg}>No match found. Check the spelling, or contact us for help.</div>
            )}
            {!error &&
              results &&
              results.map((r, i) => (
                <a key={i} href={r.link} style={styles.resultRow}>
                  <div style={styles.resultName}>{r.name}</div>
                  {r.masked_phone && <div style={styles.resultPhone}>{r.masked_phone}</div>}
                </a>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}